import React, { useMemo } from 'react';
import { Outlet } from 'react-router-dom';

// material-ui
import { styled, useTheme, Theme } from '@mui/material/styles';
import { AppBar, Box, Container, CssBaseline, Toolbar, useMediaQuery } from '@mui/material';

// project imports
import Breadcrumbs from 'ui-component/extended/Breadcrumbs';
import Header from './Header';
import Sidebar from './Sidebar';
import SubNav from './SubNav';
import TaskPanelDock from './TaskPanelDock';
import AlertDialogReusable from '../../components/dialog/AlertDialogReusable';
import navigation from 'menu-items';
import useConfig from 'hooks/useConfig';
import { appDrawerWidth, drawerWidth } from 'store/constant';
import { openDrawer } from 'store/slices/menu';
import { useDispatch, useSelector } from 'store';

// assets
import { IconChevronRight } from '@tabler/icons';
import Snackbar from 'ui-component/extended/Snackbar';
import AlertDialogProvider from 'contexts/AlertDialogContext';
import LAYOUT_CONST from 'constants/layout';

interface MainStyleProps {
    theme: Theme;
    open: boolean;
    layout: string;
    pinned: boolean;
}

// width of <main>, subtracting the pinned task dock when it is docked alongside
const mainWidth = (pinned: boolean) => (pinned ? `calc(100% - ${drawerWidth}px - ${appDrawerWidth}px)` : `calc(100% - ${drawerWidth}px)`);

// styles
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' && prop !== 'pinned' })(
    ({ theme, open, layout, pinned }: MainStyleProps) => ({
        ...theme.typography.mainContent,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        ...(!open && {
            transition: theme.transitions.create('margin', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.shorter + 200
            }),
            [theme.breakpoints.up('md')]: {
                marginLeft: layout === LAYOUT_CONST.VERTICAL_LAYOUT ? -(drawerWidth - 72) : '20px',
                width: mainWidth(pinned),
                marginTop: layout === LAYOUT_CONST.HORIZONTAL_LAYOUT ? 135 : 88
            },
            [theme.breakpoints.down('md')]: {
                marginLeft: '20px',
                width: mainWidth(pinned),
                padding: '16px',
                marginTop: 88
            },
            [theme.breakpoints.down('sm')]: {
                marginLeft: '10px',
                width: mainWidth(pinned),
                padding: '16px',
                marginRight: '10px',
                marginTop: 88
            }
        }),
        ...(open && {
            transition: theme.transitions.create('margin', {
                easing: theme.transitions.easing.easeOut,
                duration: theme.transitions.duration.shorter + 200
            }),
            marginLeft: layout === LAYOUT_CONST.HORIZONTAL_LAYOUT ? '20px' : 0,
            marginTop: layout === LAYOUT_CONST.HORIZONTAL_LAYOUT ? 135 : 88,
            width: mainWidth(pinned),
            [theme.breakpoints.up('md')]: {
                marginTop: layout === LAYOUT_CONST.HORIZONTAL_LAYOUT ? 135 : 88
            },
            [theme.breakpoints.down('md')]: {
                marginLeft: '20px',
                marginTop: 88
            },
            [theme.breakpoints.down('sm')]: {
                marginLeft: '10px',
                marginTop: 88
            }
        })
    })
);

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
    const theme = useTheme();
    const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));

    const dispatch = useDispatch();
    const { drawerOpen } = useSelector((state) => state.menu);
    const { drawerType, container, layout, taskPanelPinned } = useConfig();

    React.useEffect(() => {
        if (drawerType === LAYOUT_CONST.DEFAULT_DRAWER) {
            dispatch(openDrawer(true));
        } else {
            dispatch(openDrawer(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drawerType]);

    React.useEffect(() => {
        if (drawerType === LAYOUT_CONST.DEFAULT_DRAWER) {
            dispatch(openDrawer(true));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        dispatch(openDrawer(!matchDownMd));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [matchDownMd]);

    const condition = layout === LAYOUT_CONST.HORIZONTAL_LAYOUT && !matchDownMd;

    const header = useMemo(
        () => (
            <Toolbar sx={{ p: condition ? '10px' : '10px' }}>
                <Header />
            </Toolbar>
        ),
        []
    );

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            {/* header */}
            <AppBar
                enableColorOnDark
                position="fixed"
                color="inherit"
                elevation={0}
                sx={{
                    bgcolor: theme.palette.background.default,
                    transition: drawerOpen ? theme.transitions.create('width') : 'none'
                }}
            >
                {header}
            </AppBar>

            {/* horizontal menu-list bar */}
            {/* {layout === LAYOUT_CONST.HORIZONTAL_LAYOUT && !matchDownMd && <HorizontalBar />} */}

            {/* drawer */}
            {(layout === LAYOUT_CONST.VERTICAL_LAYOUT || matchDownMd) && <Sidebar />}

            {/* main content */}
            <Main theme={theme} open={drawerOpen} layout={layout} pinned={taskPanelPinned && !matchDownMd}>
                {/* breadcrumb */}
                {container && (
                    <Container maxWidth="lg">
                        <Breadcrumbs separator={IconChevronRight} navigation={navigation} icon title rightAlign />
                        <Outlet />
                    </Container>
                )}
                {!container && (
                    <>
                        {/* <Breadcrumbs separator={IconChevronRight} navigation={navigation} icon title rightAlign /> */}
                        <Snackbar />
                        <SubNav />
                        <AlertDialogProvider>
                            <AlertDialogReusable />
                            <Outlet />
                        </AlertDialogProvider>
                    </>
                )}
            </Main>

            {/* right-hand Tasks dock — cross-route singleton; inline push-content at md+, overlay below md */}
            {taskPanelPinned && <TaskPanelDock overlay={matchDownMd} />}
        </Box>
    );
};

export default MainLayout;
